import {differenceInSeconds} from 'date-fns';
import {flatten, min, uniq} from 'lodash';
import {action, computed, observable} from 'mobx';
import {observer} from 'mobx-react';
import {
    addDurationToDate,
    autoinject,
    bind,
    CommonAppStore,
    componentReaction,
    Duration,
    IMultiSelectOption,
    InfiniteScroll,
    inject,
    NothingHere,
    subDurationFromDate,
} from 'Mri.Web.Common';
import {stringify} from 'qs';
import * as React from 'react';
import {RouteComponentProps} from 'react-router';
import {AppStore} from '../../Core/AppStore';
import {defaultDisplayFilter, DisplayFiltersStore} from '../../DisplayFilters/DisplayFiltersStore';
import {DisplayListViewModel} from '../../DisplayFilters/DisplayListViewModel';
import {DisplayViewTypes} from '../../DisplayFilters/DisplayViewSelector';
import {IWhereClause} from '../../DisplayFilters/WhereFilter';
import {DisplayListMap} from '../DisplayListMap';
import {DisplaysChartsStore} from '../DisplayView/LiveCharts/DisplaysChartsStore';
import {LiveChartCard} from '../DisplayView/LiveCharts/LiveChartCard';
import {LiveChartGridOptions} from '../DisplayView/LiveCharts/LiveChartsGridSelector';
import {LiveChartsTopBar} from '../DisplayView/LiveCharts/LiveChartsTopBar';
import {DisplayTopAggregates} from '../DisplayTopAggregates';
import {BsTv} from '@react-icons/all-files/bs/BsTv';
import {DisplayGridNav} from './DisplayGridNav';
import {DisplayGrid} from './DisplayGrid';
import {DisplayGridViewTypes} from './DisplayGridViewSelector';

export interface IDisplayGridViewProps extends RouteComponentProps<undefined>{ }

@autoinject
@observer
export class DisplayGridView extends React.Component<IDisplayGridViewProps> {
    public constructor(props: IDisplayGridViewProps) {
        super(props);

        this.vm = new DisplayListViewModel({
            onChange: this.onChange,
            defaultDisplayFilter,
            displayFilter: this.displayFiltersStore.storage.displayFilter,
            displayEventFilter: undefined,
            sort: this.displayFiltersStore.storage.sort,
            where: this.displayFiltersStore.storage.where,
            gridViewType: this.displayFiltersStore.storage.gridViewType,
            interval: 30000,
            subscribe: true,
            snapshot: true,
            showStickyColumns: true,
            pageSize: this.displayFiltersStore.storage.gridViewType == DisplayGridViewTypes.LiveChartsView ? 4 : this.displayFiltersStore.storage.gridViewType == DisplayGridViewTypes.MapView ? 500 : 100,
            isGrid: true,
        });
        
        this.vm.onQueryChange(props.location.search.slice(1), false);

        componentReaction(
            this,
            () => this.props.location.search,
            s => this.vm.onQueryChange(s.slice(1))
        );
        componentReaction(this, () => this.firstAvailableSampleTime, this.initStartTime);
    }

    @action
    public async componentDidMount() {
        try {
            //Need to load filter dependents before displays/rows
            await this.vm.loadAccountsAsync();
            await this.vm.loadContractsAsync();
            await this.vm.loadAllOptionsAsync();
            await this.vm.loadOptionsAsync();
            if (this.vm.gridViewType == DisplayGridViewTypes.MapView) await this.vm.loadGpsAsync();
            if (this.vm.gridViewType != DisplayGridViewTypes.Grid) await this.vm.loadMoreAsync();

            if (this.vm.gridViewType == DisplayGridViewTypes.LiveChartsView) {
                let refreshRate = this.appStore.storage.liveChartSettings.secsPerSample > 60 ?  this.appStore.storage.liveChartSettings.secsPerSample * 2000 : 120000;
                
                this.refreshTimer = window.setInterval(this.refreshTopBar, refreshRate);
            }
        } catch (e: any) {
            this.appStore.setError(e);
        } 
    }

    public componentWillUnmount() {
        if (this.refreshTimer) window.clearInterval(this.refreshTimer);

        this.vm.dispose();
    }

    public render() {
        const { hasMore, busy, displays } = this.vm;

        return (
            <div className="container-fluid p-0 h-100">
                <DisplayTopAggregates vm={this.vm} />

                {/*Header*/}

                <DisplayGridNav vm={this.vm} />

                {this.vm.gridViewType == DisplayGridViewTypes.LiveChartsView &&
                    this.endTime &&
                    this.startTime &&
                    this.firstAvailableSampleTime && (
                        <LiveChartsTopBar
                            vm={this.vm}
                            onStartTimeChange={this.onStartTimeChange}
                            onTimeWindowChange={this.onTimeWindowChange}
                            startTime={this.startTime}
                            endTime={this.endTime}
                            sliderMin={this.firstAvailableSampleTime.valueOf()}
                            hasMoreDataThanWindow={this.hasMoreDataThanWindow}
                            liveRefresh={this.liveRefresh}
                            maxValue={this.maxValue}
                            timeWindow={this.timeWindow}
                            chartNames={this.chartNames}
                            serialNumber={this.singleDisplaySearch?.serialNumber || displays[0].serialNumber}
                        />
                    )}

                {/*Body*/}

                {this.vm.gridViewType == DisplayGridViewTypes.Grid &&
                    <DisplayGrid vm={this.vm} {...this.props} />
                }

                {this.vm.gridViewType == DisplayGridViewTypes.MapView && (
                    <DisplayListMap displays={displays} vm={this.vm} history={this.props.history} />
                )}

                {this.vm.gridViewType == DisplayGridViewTypes.LiveChartsView && (
                    <InfiniteScroll
                        throttle={100}
                        threshold={300}
                        hasMore={hasMore}
                        isLoading={busy}
                        container={this.appStore.mainContainer}
                        onLoadMore={this.loadMoreAsync}
                    >
                        {this.vm.gridViewType == DisplayGridViewTypes.LiveChartsView && (
                            <div className="container-fluid py-1">
                                <div className="row px-1">
                                    {displays.map((d, k) => (
                                        <div className={this.gridClass} style={this.gridStyle} key={d.serialNumber}>
                                            <LiveChartCard
                                                chartNumber={k + 1}
                                                startTime={this.startTime}
                                                className="h-100"
                                                display={d}
                                                vm={this.vm}
                                                history={this.props.history}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                       
                        <NothingHere
                            empty={!displays.length}
                            forceLoaderOnLoad
                            icon={<BsTv />}
                            label="No Displays Returned. Try Widening Your Search."
                            loading={busy || this.loading}
                            loadingMessage="Loading..."
                            className="position-fixed"
                        />
                    </InfiniteScroll>
                )}
            </div>
        );
    }

    @bind
    private onChange(
        displayFilter: Mri.DisplayFilters.DisplayFilter,
        displayEventFilter: Mri.DisplayEvents.DisplayEventFilter,
        sort: string[],
        where: IWhereClause[] | undefined,
        columns: IMultiSelectOption<string | Mri.WebApi.MeasurementId>[],
        configColumns: IMultiSelectOption<string | Mri.WebApi.MeasurementId>[],
        viewType: DisplayViewTypes,
        gridViewType: DisplayGridViewTypes
    ) {
        this.displayFiltersStore.storage.displayFilter = displayFilter;
        this.displayFiltersStore.storage.displayEventFilter = displayEventFilter;
        this.displayFiltersStore.storage.sort = sort;
        this.displayFiltersStore.storage.where = where;
        this.displayFiltersStore.storage.gridViewType = gridViewType;

        if (this.vm.isDisposed) return;

        const query = stringify({
            displayFilter: JSON.stringify(displayFilter),
            displayEventFilter: JSON.stringify(displayEventFilter),
            sort: JSON.stringify(sort),
            where: JSON.stringify(where),
        });
        this.props.history.push({
            pathname: this.props.location.pathname,
            search: '?' + query,
        });
    }

    @bind
    @action
    private loadMoreAsync() {
        this.vm.loadMoreAsync();
    }

    /**
     * This is called when the user changes the start time of the chart.
     * On the first call, the start time is initialized to the user's saved start time, if liveChartEnableStartTime
     * is set in the app store. This is done because the start time is not actually initialized when the component is
     * loaded for the first time.
     * @param startTime
     * @private
     */
    @bind
    @action
    private onStartTimeChange(startTime: Date) {
        this.startTime = startTime;
    }

    @bind
    @action
    private initStartTime() {
        if (!this.startTime && this.firstAvailableSampleTime) {
            this.startTime = this.maxValue;
        }
    }

    @bind
    @action
    private onTimeWindowChange() {
        this.startTime = this.maxValue;
    }

    @computed
    private get firstAvailableSampleTime() {
        const f = min(this.chartConfigs.map(c => c.firstAvailableSampleTime.valueOf()));

        return f ? new Date(f) : undefined;
    }
    
    @computed
    private get chartNames() {
        return uniq(flatten(this.chartConfigs.map(c => c.chartConfigs && c.chartConfigs.map(x => x.chartName))));
    }

    @computed
    private get hasMoreDataThanWindow() {
        if (!this.firstAvailableSampleTime) return false;

        return this.firstAvailableSampleTime < this.maxValue;
    }

    @computed
    private get endTime() {
        if (!this.startTime) return;
        
        return addDurationToDate(this.startTime, this.timeWindow);
    }

    @computed
    private get liveRefresh() {
        if (!this.startTime) return false;
        return differenceInSeconds(this.maxValue, this.startTime) < 60;
    }

    @bind
    @action
    private async refreshTopBar() {
        if (!this.liveRefresh || this.loading) return;

        console.info('Refresh Charts');
        this.now = new Date();
        this.startTime = this.maxValue;
    }

    @computed
    private get tableClassName() {
        let className = 'table table-striped fixed-header display-list';

        if (this.vm.viewType == DisplayViewTypes.TableSmall) className += ' table-sm';
        if (this.vm.viewType == DisplayViewTypes.TableExtraSmall) className += ' table-sm table-extra-small';

        return className;
    }

    @computed
    private get chartConfigs() {
        return this.vm.displays.map(d => this.displaysChartsStore.displaysCharts.get(d.serialNumber)!).filter(c => !!c);
    }

    @computed
    private get timeWindow() {
        //TODO needs to be investigated why props.appStore.storage.liveChartTimeWindow undefined
        return new Duration((this.appStore.storage.liveChartSettings.liveChartTimeWindow ?? 2) * 60 * 60 * 1000);
    }

    @computed
    private get maxValue() {
        return subDurationFromDate(this.now, this.timeWindow);
    }

    @computed
    private get singleDisplaySearch() {
        if (this.vm.displays.length == 1) {
            return this.vm.displays[0];
        }
    }

    @computed
    private get gridClass() {
        switch (this.appStore.storage.liveChartSettings.liveChartSelectedGrid) {
            default:
                return 'col-6 p-1';
            case LiveChartGridOptions.OneByOne:
                return 'col-12 p-1';
            case LiveChartGridOptions.TwoByTwo:
                return 'col-6 p-1';
            case LiveChartGridOptions.ThreeByThree:
                return 'col-4 p-1';
            case LiveChartGridOptions.FourByFour:
                return 'col-3 p-1';
        }
    }

    @computed
    private get gridStyle() {
        switch (this.appStore.storage.liveChartSettings.liveChartSelectedGrid) {
            default:
                return { height: `calc(50vh - ${(this.commonAppStore.headerHeight || 0) / 2 + 4}px)` };
            case LiveChartGridOptions.OneByOne:
                return { height: `calc(100vh - ${(this.commonAppStore.headerHeight || 0) + 4}px)` };
            case LiveChartGridOptions.TwoByTwo:
                return { height: `calc(50vh - ${(this.commonAppStore.headerHeight || 0) / 2 + 4}px)` };
            case LiveChartGridOptions.ThreeByThree:
                return { height: `calc(33vh - ${(this.commonAppStore.headerHeight || 0) / 3 + 4}px)` };
            case LiveChartGridOptions.FourByFour:
                return { height: `calc(25vh - ${(this.commonAppStore.headerHeight || 0) / 4 + 4}px)` };
        }
    }

    @observable
    private loading = false;
    @observable
    private now = new Date();
    @observable
    private startTime?: Date;
    private refreshTimer?: number;
    private vm: DisplayListViewModel;
    @inject
    private appStore!: AppStore;
    @inject
    private displayFiltersStore!: DisplayFiltersStore;
    @inject
    private displaysChartsStore!: DisplaysChartsStore;
    @inject
    private commonAppStore!: CommonAppStore;
}